import type { AnimationClip, ClipKeyframe } from '@/types/animationClip'
import type { AnimationLayer } from '@/types/animationLayer'

/**
 * 在给定全局时刻解析应使用的屏幕图栈项 `id`（用于绑定 `THREE.Texture`）。
 *
 * 规则（**阶梯函数**，与用户期望一致）：
 * - 仅当 `layer.screenMediaType === 'image'` 且 `screenImages.length > 0` 时返回栈内 id；否则返回 `null`。
 * - 时间轴：与 {@link evaluateLayerPoseAtGlobalTime} 相同的 clip 遍历顺序；在某一 clip 内取「最后一个 `timeInClip <= local`」的关键帧的 `screenImageId`。
 * - 若该关键帧 `screenImageId` 为空 / 未设置：继承「进入该 clip 之前」已生效的绑定；若仍无，则使用栈内第一张的 `id`。
 * - **首关键帧前导段**：当 `t` 早于「首个 clip 的首个关键帧绝对时刻」时，仍使用首关键帧的 `screenImageId`
 *   （即首关键帧同时覆盖时间轴起点至自身的整段）。
 *
 * @param layer - 动画图层
 * @param globalTimeSeconds - 全局时间（秒）
 */
export function resolveScreenImageIdAtGlobalTime(
  layer: AnimationLayer,
  globalTimeSeconds: number,
): string | null {
  if (layer.screenMediaType !== 'image' || layer.screenImages.length === 0) return null
  const defaultId = layer.screenImages[0]!.id
  const t = Number(globalTimeSeconds)
  const clipsSorted = [...layer.clips].sort((a, b) => a.startSec - b.startSec)
  if (clipsSorted.length === 0) return defaultId
  if (t < clipsSorted[0]!.startSec) {
    /** 首 clip 之前：等价于「在首 clip 的 local=0 处」解析，让首关键帧覆盖前导段。 */
    return resolveScreenImageIdInClip(clipsSorted[0]!, 0, null, defaultId)
  }

  let inheritedId: string | null = null

  for (const c of clipsSorted) {
    if (t < c.startSec) {
      return inheritedId ?? defaultId
    }
    const end = c.startSec + c.durationSec
    const local = Math.min(c.durationSec, Math.max(0, t - c.startSec))
    if (t <= end + 1e-9) {
      return resolveScreenImageIdInClip(c, local, inheritedId, defaultId)
    }
    inheritedId = resolveScreenImageIdInClip(c, c.durationSec, inheritedId, defaultId)
  }

  return inheritedId ?? defaultId
}

/**
 * @param clip - 动画片段
 * @param localSeconds - clip 内本地时间
 * @param inheritedBeforeClip - 进入该 clip 前的继承绑定
 * @param stackDefaultId - 栈内首张 id
 */
function resolveScreenImageIdInClip(
  clip: AnimationClip,
  localSeconds: number,
  inheritedBeforeClip: string | null,
  stackDefaultId: string,
): string | null {
  const sorted = [...clip.keyframes].sort((a, b) => a.timeInClip - b.timeInClip)
  if (sorted.length === 0) return inheritedBeforeClip ?? stackDefaultId
  let pick: ClipKeyframe = sorted[0]!
  for (const k of sorted) {
    if (k.timeInClip <= localSeconds + 1e-9) pick = k
    else break
  }
  const explicit = pick.screenImageId
  if (explicit != null && explicit !== '') return explicit
  return inheritedBeforeClip ?? stackDefaultId
}
